import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import difference from 'lodash/difference';
import trim from 'lodash/trim';
import includes from 'lodash/includes';
import toLower from 'lodash/toLower';
import { Popover, Popconfirm, Tooltip, Radio, Icon } from 'antd';
import Currency from '$app/components/vendor/Currency';
import ActionButton from '$app/apps/bookings/components/ActionButton';
import { poundsToPence } from '$app/utils/util';
import { BOOKING_STATUS, GARAGE_STATUS } from '$app/apps/bookings/containers/BookingPage/constants';
import WorkOperationsTable from './WorkOperationsTable';
import Markup from './Markup';
import Status from './Status';
import BubbleIcon from './bubble-chat-typing.svg';
import DisplayDiff from './DisplayDiff';
import {
  COMPLETE_LIMIT,
  TRANSPORTATION_FEE_TYPE,
  ADJUSTMENT_TYPE,
  COLLECTION_AND_DELIVERY_TYPE,
  DISCOUNT_TYPE,
} from '../../constants';
import { getLineItemQuantity } from '../../util';
import brandsImgMapping from './brandsImgMapping';
import { invitationIsReadonly } from '../../../utils';

export const renderLabel = (label) => <span style={{ opacity: 0.6, fontSize: 12 }}>{label}</span>;

const setRowStatus = (i) => ({
  ...i,
  className: [...(i.className || []), `status-${i.status}`],
});

const makeGroup = (groupName, type) => ({
  key: `group-${type}`,
  type: 'label',
  workSummary: groupName,
  className: `group group-${type}`,
  render: (cols, key) => {
    if (cols.columnKey !== 'workOperation') return { props: { colSpan: 0 } };
    return {
      children: <i style={{ opacity: 0.6, fontWeight: 'normal' }}>{groupName}</i>,
      props: {
        colSpan: 11,
      },
    };
  },
});

const isWorkOperation = ({ type }) => ['mot', 'service', 'other'].includes(type);

const commentListItem = (time, message, by) => (
  <li key={`${time}-${message}`}>
    <b>
      <small>
        {(time && moment(time).format('HH:mm')) || 'unknown'} {by} -{' '}
      </small>
    </b>
    {message}
  </li>
);

const getAdditionalDiscountDisplayInfo = (workOperation) => {
  if (workOperation.appliedToCheckout && workOperation.appliedToEWO) {
    return 'Applied to checkout, Maia & EWO operations';
  }
  if (workOperation.appliedToCheckout) {
    return 'Applied to checkout operations only';
  }
  if (workOperation.appliedToEWO) {
    return 'Applied to Maia & EWO operations only';
  }

  return undefined;
};

const CATEGORY_LABELS = {
  employee_discount: 'Employee Discount',
  return_to_workshop: 'Return to workshop',
  company_car: 'Company Car',
  cancellation_prevention: 'Cancellation Prevention',
  service_advisors: 'Service Advisors',
  customer_service: 'Customer Service',
  supply_team: 'Supply Team',
  other: 'Other',
};

const getFormattedCommentString = (comments = [], record = {}) =>
  comments.map((i) => {
    if (i.type && i.type === 'cm-comment') return commentListItem(i.updatedAt, i.message, 'Fixter');
    if (i.type && i.type === 'cm-additional-discount-category') {
      return (
        <li key={`${i.updatedAt}-${i.message}`}>
          <div>
            <b>Discount Category:</b> {CATEGORY_LABELS[i.message]}
          </div>
        </li>
      );
    }
    if (i.type && i.type === 'cm-additional-discount') {
      return (
        <li key={`${i.updatedAt}-${i.message}`}>
          <div style={{ marginTop: '4px' }}>
            <b>Comment:</b> {i.message}
          </div>
        </li>
      );
    }
    return commentListItem(i.updatedAt, i.message, 'Garage');
  });

const hasComments = (comments = []) =>
  !isEmpty(comments) && comments.filter((i) => !!trim(i.message)).length > 0;

const renderProductName = (val, type, data) => {
  const { vehicle, workOperation } = data;
  const { motStatus, motCompletedDateTime } = vehicle;
  if (toLower(type) === 'mot' && motStatus && motCompletedDateTime) {
    const motCompleted = motCompletedDateTime && moment().diff(motCompletedDateTime, 'days');
    let pendingMotState = 'Pending';
    let date = '';
    if (motCompleted < COMPLETE_LIMIT) {
      pendingMotState = toLower(motStatus) === 'passed' ? 'Passed' : 'Failed';
      date = moment(motCompletedDateTime).format('DD/MM/YYYY HH:mm');
    }
    return (
      <span>
        {val}
        <span className={`mot_result ${toLower(pendingMotState)}`}>
          {pendingMotState} <span className="mot_date">{date}</span>
        </span>
      </span>
    );
  }
  if (['coupon', 'discount'].includes(toLower(type))) {
    const additionalDiscountDisplayInfo = getAdditionalDiscountDisplayInfo(workOperation);
    return (
      <span>
        {val} {additionalDiscountDisplayInfo && `(${additionalDiscountDisplayInfo})`}
      </span>
    );
  }
  return val;
};

class WorkDetailsTable extends React.Component {
  state = {
    collapsedKeys: [],
    approvedWork: null,
    declinedWork: null,
  };

  woSummaryRender = (val, wo) => {
    const { sku, key, type, parentId } = wo;

    const data = {
      vehicle: this.props.vehicle,
      workOperation: wo,
    };

    return !sku &&
      typeof key === 'number' && // exlcude Total
      !includes([COLLECTION_AND_DELIVERY_TYPE, ADJUSTMENT_TYPE, TRANSPORTATION_FEE_TYPE], type) && // exclude Garage C&D and Adjustments
      !parentId // exclude children
      ? `Other - ${val}`
      : renderProductName(val, type, data);
  };

  get columns() {
    return [
      {
        title: '',
        key: 'description-group',
        children: [
          {
            title: 'Quote',
            key: 'workOperation',
            dataIndex: 'workSummary',
            width: '34%',
            render: (val, wo) => {
              const { comments } = wo;

              if (!hasComments(comments)) return this.woSummaryRender(val, wo);

              return (
                <>
                  <span style={{ paddingRight: 16 }}>{this.woSummaryRender(val, wo)}</span>
                  <Popover
                    content={
                      <ul
                        style={{
                          maxWidth: 380,
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                        }}
                      >
                        {getFormattedCommentString(comments, wo)}
                      </ul>
                    }
                    trigger="hover"
                  >
                    <BubbleIcon width={22} height={22} style={{ cursor: 'pointer', verticalAlign: 'middle', overflow: 'visible' }} />
                  </Popover>
                </>
              );
            },
          },
        ],
      },
      {
        title: 'Garage',
        key: 'garage-group',
        children: [
          {
            title: 'Brand Name',
            key: 'brandName',
            align: 'right',
            width: '10%',
            render: (val, record) => {
              if (val) {
                const srcVal = val.trim().split(' ').join('');
                const imgSrc = brandsImgMapping[srcVal];
                return (
                  <>
                    {imgSrc ? (
                      <img
                        style={{ maxHeight: 30, maxWidth: 60, marginRight: '7px' }}
                        src={brandsImgMapping[srcVal]}
                        alt={record.brandName}
                      />
                    ) : (
                      `${val} `
                    )}
                    {record.partReferenceNumber && (
                      <Tooltip title={record.partReferenceNumber} placement="right">
                        <Icon type="info-circle" />
                      </Tooltip>
                    )}
                  </>
                );
              }
              return null;
            },
          },
          {
            title: 'Qty',
            key: 'partsQuantity',
            align: 'right',
            width: '3%',
            render: (val, record) => {
              const { partsQuantity, labourTime, metadata: m = {}, type, quoteType } = record;
              if (labourTime && labourTime !== 0 && partsQuantity && partsQuantity !== 0) {
                return (
                  <>
                    {renderLabel('labour')} {labourTime}
                    <br />
                    <br />
                    {renderLabel('part')} {getLineItemQuantity(record)}
                  </>
                );
              }

              if (type === 'part') {
                return getLineItemQuantity(record);
              }

              if (type === 'labour') {
                /**
                 * Check to see if the garage has altered the
                 * suggested labour time
                 */
                if (labourTime != m.initialLabourTime && quoteType === 'proposed') {
                  return (
                    <DisplayDiff
                      fromValue={m.initialLabourTime}
                      toValue={labourTime}
                      source={m.providedBy || 'Autodata'}
                    />
                  );
                }

                return labourTime;
              }
              return '';
            },
          },
          {
            title: 'Price',
            key: 'partsPrice',
            displayPrice: true,
            width: '6%',
            align: 'right',
            render: (val, { partsPrice, labourCost, metadata: m = {}, type, quoteType }) => {
              if (labourCost && labourCost !== 0 && partsPrice && partsPrice !== 0) {
                return (
                  <>
                    {renderLabel('labour')} <Currency trailingZero>{poundsToPence(labourCost)}</Currency>
                    <br />
                    <br />
                    {renderLabel('part')} <Currency trailingZero>{poundsToPence(partsPrice)}</Currency>
                  </>
                );
              }

              if (type === 'part') {
                return <Currency trailingZero>{poundsToPence(partsPrice)}</Currency>;
              }

              if (type === 'labour') {
                /**
                 * Check to see if the garage has altered the
                 * suggested labour cost
                 */
                if (m.initialLabourCost != poundsToPence(labourCost) && quoteType === 'proposed') {
                  return (
                    <DisplayDiff
                      fromValue={m.initialLabourCost}
                      toValue={poundsToPence(labourCost)}
                      isCurrency
                      source={m.providedBy}
                    />
                  );
                }
                return <Currency trailingZero>{poundsToPence(labourCost)}</Currency>;
              }

              return '';
            },
          },
          {
            title: 'Total (pre VAT)',
            key: 'garageCost',
            displayPrice: true,
            align: 'right',
            width: '9%',
            render: (val, wo) => {
              const { type, metadata = {}, garageCost, quoteType, workSummary } = wo;
              const { initialLabourTime, initialLabourCost } = metadata;

              if (type === ADJUSTMENT_TYPE && workSummary === 'Garage adjustment') return null;

              if (type === 'labour') {
                /**
                 * Check to see if the cost was changed by the garage
                 */
                const initialGarageTotal = (initialLabourTime || 0) * (initialLabourCost || 0);
                if (
                  Math.round(initialGarageTotal) !== poundsToPence(garageCost) &&
                  quoteType === 'proposed'
                ) {
                  return (
                    <DisplayDiff
                      fromValue={initialGarageTotal}
                      toValue={poundsToPence(val)}
                      isCurrency
                      source={metadata.providedBy}
                    />
                  );
                }
              }

              return <Currency trailingZero>{poundsToPence(val)}</Currency>;
            },
          },
          {
            title: 'Total (inc. VAT)',
            key: 'garageCostWithVat',
            displayPrice: true,
            width: '9%',
            align: 'right',
            render: (val, wo) => {
              const { type, metadata = {}, garageCost, quoteType, workSummary } = wo;
              const { initialLabourTime, initialLabourCost } = metadata;

              if (type === ADJUSTMENT_TYPE && workSummary === 'Garage adjustment') return null;

              if (type === 'labour') {
                /**
                 * Check to see if the cost was changed by the garage
                 */
                const initialGarageTotal = (initialLabourTime || 0) * (initialLabourCost || 0);
                if (
                  Math.round(initialGarageTotal) !== poundsToPence(garageCost) &&
                  quoteType === 'proposed'
                ) {
                  return (
                    <DisplayDiff
                      fromValue={initialGarageTotal}
                      toValue={poundsToPence(val)}
                      isCurrency
                      source={metadata.providedBy}
                    />
                  );
                }
                return <Currency trailingZero>{poundsToPence(val)}</Currency>;
              }

              return <Currency trailingZero>{poundsToPence(val)}</Currency>;
            },
          },
        ],
      },
      {
        title: 'Customer',
        key: 'customer-group',
        align: 'center',
        children: [
          {
            title: 'Total Inc. VAT',
            key: 'fixedCostWithVAT',
            displayPrice: true,
            width: '9%',
            align: 'center',
            render: (val, record) => {
              if (`${val}`.includes('%')) return val;
              if (!['service', 'mot', 'other', 'part', 'labour'].includes(record.type)) return 'default';

              const isParent = record.children;
              const parent = this.dataSource.find((op) => op.operationId === record.parentId);
              const sku = get(record, 'sku') || get(parent, 'sku');
              const price = get(this.props, 'originalWorkOperations.list', []).find(
                (op) => sku && op.sku === sku
              );

              const matched = isParent
                ? price
                : get(price, 'subLineItems', []).find(
                    ({ title }) => title === record.title || title === record.workSummary
                  );

              const customerPriceConfidence = get(price, 'customerPriceConfidence', 0);
              const garageFixedCostWithVAT = get(record, 'fixedCostWithVAT', 0);
              const customerFixedCostWithVAT = get(matched, 'fixedCostWithVAT', 0);

              const priceDifference = garageFixedCostWithVAT - customerFixedCostWithVAT;
              const relativeDifference = 100 - (garageFixedCostWithVAT / customerFixedCostWithVAT) * 100;
              const lowerDifferenceTreshold = -10;
              const upperDifferenceTreshold = 10;
              const highlightPrice = !(
                matched &&
                relativeDifference > lowerDifferenceTreshold &&
                relativeDifference < upperDifferenceTreshold
              );

              return (
                <Popover
                  placement="right"
                  trigger="hover"
                  content={
                    <div>
                      <p>
                        {' '}
                        Customer price at booking:{' '}
                        <b>
                          {matched ? (
                            <Currency trailingZero>{poundsToPence(customerFixedCostWithVAT)}</Currency>
                          ) : (
                            'Not generated by QE'
                          )}{' '}
                        </b>{' '}
                      </p>
                      <p>
                        {' '}
                        Garage submitted difference:
                        <b>
                          {' '}
                          {matched ? (
                            <Currency trailingZero>{poundsToPence(priceDifference)}</Currency>
                          ) : (
                            'N/A'
                          )}{' '}
                        </b>
                      </p>
                      <p>
                        {' '}
                        Offline quote displayed:{' '}
                        <b> {matched && customerPriceConfidence < 1 ? 'Yes' : 'No'} </b>{' '}
                      </p>
                    </div>
                  }
                >
                  <div style={{ color: highlightPrice ? 'red' : 'black' }}>
                    {' '}
                    <Currency trailingZero>{poundsToPence(garageFixedCostWithVAT)}</Currency>
                  </div>
                </Popover>
              );
            },
          },
          {
            title: 'Mark up',
            key: 'markup',
            align: 'center',
            width: '9%',
            render: (val, { key, parentId, type, garageCostWithVat, fixedCostWithVAT }) =>
              ![COLLECTION_AND_DELIVERY_TYPE, DISCOUNT_TYPE, TRANSPORTATION_FEE_TYPE].includes(type) && (
                <Markup garageCostWithVat={garageCostWithVat} fixedCostWithVAT={fixedCostWithVAT} />
              ),
          },
        ],
      },
      {
        title: '',
        key: 'action-group',
        children: [
          {
            title: 'Status',
            key: 'status',
            align: 'center',
            render: (val, item) => {
              const { parentId, bookingGarageStatus, workStatus, quoteType, createdAt } = item;

              if (parentId) {
                // Created At on children only
                return (
                  <Tooltip placement="left" title="Time &amp; date added">
                    <time
                      dateTime={val}
                      style={{ cursor: 'pointer', fontSize: 9, opacity: 0.4, fontWeight: 'bold' }}
                    >
                      {moment(createdAt).format('DD/MM HH:mm')}
                    </time>
                  </Tooltip>
                );
              }
              if (quoteType !== 'proposed') {
                return <Status>{workStatus || val}</Status>;
              }
              if (bookingGarageStatus === 'FIXTER_REVIEW' && !['accepted', 'declined'].includes(workStatus)) {
                if (item.comments.find((c) => c.type === 'cm-comment')) {
                  return <Status>Reviewed</Status>;
                }
                return <Status>To review</Status>;
              }
              if (['CUSTOMER_REVIEW', 'CUSTOMER_REVIEWED'].indexOf(bookingGarageStatus) >= 0) {
                return (
                  <Radio.Group
                    value={workStatus}
                    onChange={(evt) => this.setWorkStatus(item, evt)}
                    size="small"
                    style={{ fontSize: 12, width: 140 }}
                  >
                    <Popconfirm
                      title="Are you sure you want to change customer's decision?"
                      placement="topLeft"
                      onConfirm={this.handleEditWorkStatus}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Radio.Button
                        value="approved"
                        style={
                          workStatus === 'approved'
                            ? { borderColor: '#b7eb8f', color: '#52c41a', background: '#f6ffed' }
                            : {}
                        }
                      >
                        Approved
                      </Radio.Button>
                    </Popconfirm>
                    <Popconfirm
                      title="Are you sure you want to change customer's decision?"
                      placement="topLeft"
                      onConfirm={this.handleEditWorkStatus}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Radio.Button
                        value="declined"
                        style={
                          workStatus === 'declined'
                            ? { borderColor: '#ffbb96', color: '#fa541c', background: '#fff2e8' }
                            : {}
                        }
                      >
                        Declined
                      </Radio.Button>
                    </Popconfirm>
                  </Radio.Group>
                );
              }
              return <Status>{workStatus}</Status>;
            },
          },
          {
            title: '',
            align: 'right',
            key: 'actions',
            width: 90,
          },
        ],
      },
    ];
  }

  setWorkStatus = (item, evt) => {
    const workStatus = evt.target.value;

    if (workStatus === 'approved') this.setState({ approvedWork: item, declinedWork: null });
    if (workStatus === 'declined') this.setState({ declinedWork: item, approvedWork: null });
  };

  handleEditWorkStatus = () => {
    const { approvedWork, declinedWork } = this.state;

    if (approvedWork) this.props.handleApprove(approvedWork);
    if (declinedWork) this.props.handleDecline(declinedWork);
  };

  isEditableWorkOperation = (wo) => {
    const { garageBillStatus } = this.props;
    if (!isWorkOperation(wo)) {
      return false;
    }
    if (this.isDeclinedWorkOperation(wo)) {
      return false;
    }
    if (this.isProposedWorkOperation(wo)) {
      return wo.bookingGarageStatus === GARAGE_STATUS.FIXTER_REVIEW;
    }
    if (garageBillStatus === 'on_hold') {
      return true;
    }
    const { bookingStatus } = this.props;
    return !(
      bookingStatus === BOOKING_STATUS.PENDING ||
      (bookingStatus >= BOOKING_STATUS.WORK_COMPLETE && bookingStatus !== BOOKING_STATUS.GARAGE_DECLINED)
    );
  };

  isEditableAdjustment = (adjustment) => {
    if (adjustment.type !== ADJUSTMENT_TYPE) return false;
    return this.props.canDoAdjustment;
  };

  isGarageCAndDEditable = (gCAndD) => {
    const { bookingStatus, garageBillStatus } = this.props;
    if (garageBillStatus === 'on_hold') return true;
    return !(
      bookingStatus >= BOOKING_STATUS.WORK_COMPLETE && bookingStatus !== BOOKING_STATUS.GARAGE_DECLINED
    );
  };

  setLineItemRender = (lineItem) => {
    lineItem.render = {
      actions: ({ recordKey, defaultRender }) => {
        const record = this.getRecordByKey(recordKey);
        const { status, bookingGarageStatus, workStatus, comments = [] } = record;

        let EditText = 'Edit';
        if (
          bookingGarageStatus === 'FIXTER_REVIEW' &&
          !['accepted', 'declined'].includes(workStatus) &&
          !comments.find((c) => c.type === 'cm-comment')
        ) {
          EditText = 'Review';
        }

        let EditComp = <ActionButton onClick={() => this.props.onClickEdit(record)}>{EditText}</ActionButton>;
        if (
          (!this.isEditableWorkOperation(record) &&
            !this.isEditableAdjustment(record) &&
            !this.isGarageCAndDEditable(record)) ||
          record.type === 'additional-discount' ||
          invitationIsReadonly()
        ) {
          EditComp = null;
        }

        if (status === 'deleted') {
          EditComp = null;
        }

        return (
          <div className="inlineFlex">
            {defaultRender}
            {EditComp}
          </div>
        );
      },
    };
    return lineItem;
  };

  groupByUrgency = (lineItems) => {
    const mandatoryLineItems = lineItems.filter(({ urgency }) => includes(['mandatory'], toLower(urgency)));
    const dangerousLineItems = lineItems.filter(({ urgency }) => includes(['dangerous'], toLower(urgency)));
    const majorLineItems = lineItems.filter(({ urgency }) => includes(['major'], toLower(urgency)));
    const minorLineItems = lineItems.filter(({ urgency }) => includes(['minor'], toLower(urgency)));
    const advisoryLineItems = lineItems.filter(({ urgency }) => includes(['advisory'], toLower(urgency)));
    const performanceMaintenanceLineItems = lineItems.filter(({ urgency }) => includes(['performance_maintenance'], toLower(urgency)));
    const otherLineItems = lineItems.filter(
      ({ urgency }) => !includes(['dangerous', 'major', 'minor', 'advisory', 'performance_maintenance'], toLower(urgency))
    );
    return [
      !isEmpty(mandatoryLineItems) && makeGroup('Mandatory additional operations', 'dangerous'),
      ...mandatoryLineItems,
      !isEmpty(dangerousLineItems) && makeGroup('Dangerous additional operations', 'dangerous'),
      ...dangerousLineItems,
      !isEmpty(majorLineItems) && makeGroup('Major additional operations', 'major'),
      ...majorLineItems,
      !isEmpty(minorLineItems) && makeGroup('Minor additional operations', 'minor'),
      ...minorLineItems,
      !isEmpty(advisoryLineItems) && makeGroup('Advisory additional operations', 'advisory'),
      ...advisoryLineItems,
      !isEmpty(performanceMaintenanceLineItems) && makeGroup('Performance maintenance', 'performance_maintenance'),
      ...performanceMaintenanceLineItems,
      !isEmpty(otherLineItems) && makeGroup('Other additional operations', 'other'),
      ...otherLineItems,
    ].filter(Boolean);
  };

  get dataSource() {
    const {
      approvedQuoteItems,
      approvedQuoteTotals,
      proposedQuoteItems,
      proposedQuoteTotals,
      adjustmentsLine,
      campaigns,
      additionalDiscounts = [],
      credits,
      collectionDeliveryLine,
      showProposedQuote,
      feeOperation,
    } = this.props;

    const dataSource = [
      ...approvedQuoteItems.map(this.setLineItemRender),
      ...adjustmentsLine.map(this.setLineItemRender),
      ...credits,
      ...collectionDeliveryLine.map(this.setLineItemRender),
      ...feeOperation,
      ...campaigns.map(i => ({ ...i, className: [...(i.className || []), 'discount-row'] })).map(this.setLineItemRender),
      ...additionalDiscounts.map(i => ({ 
        ...i, 
        className: [...(i.className || []), 'discount-row'],
        workOperation: {
          text: i.workSummary || i.title,
        }
      })).map(this.setLineItemRender),
      ...approvedQuoteTotals,
    ];
    if (showProposedQuote && !isEmpty(proposedQuoteItems)) {
      return [
        ...dataSource,
        ...this.groupByUrgency(proposedQuoteItems.map(this.setLineItemRender)),
        ...proposedQuoteTotals,
      ];
    }
    return dataSource.map(setRowStatus);
  }

  get expandedRowKeys() {
    const keysWithChildren = this.dataSource.map(({ key, children }) => children && key);
    return difference(keysWithChildren, this.state.collapsedKeys);
  }

  getRecordByKey = (recordKey) => {
    let record = null;
    this.dataSource.some((data) => {
      if (data.key === recordKey) {
        record = data;
      } else if (!isEmpty(data.children)) {
        record = data.children.find(({ key }) => key === recordKey);
      }
      return record;
    });
    return record;
  };

  isProposedWorkOperation = (wo) => get(wo, 'quoteType') === 'proposed';

  isDeclinedWorkOperation = (wo) => get(wo, 'workStatus') === 'declined';

  handleExpandRow = (expanded, { key }) => {
    this.setState(({ collapsedKeys }) => {
      if (collapsedKeys.includes(key)) {
        return { collapsedKeys: difference(collapsedKeys, [key]) }; // expand
      }
      return { collapsedKeys: [...collapsedKeys, key] }; // collapse
    });
  };

  get isBeingReviewed() {
    const hasLineIneReview = this.dataSource.find((i) => ['FIXTER_REVIEW'].includes(i.bookingGarageStatus));
    return !!hasLineIneReview;
  }

  render() {
    return (
      <WorkOperationsTable
        parentStyledImportant
        columns={this.columns}
        dataSource={this.dataSource}
        expandedRowKeys={this.expandedRowKeys}
        onExpand={this.handleExpandRow}
        className={classNames({ 'in-review': this.isBeingReviewed })}
      />
    );
  }
}

export default WorkDetailsTable;
